'use client';

import { useId, useMemo, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MoXat, XATS_ICON_SRC, cn } from '@/components/help-center/latest-power-shared';
import { DaysStarIcon } from '@/components/help-center/fairtrade/fairtrade-shared';
import type { FairtradeChartPoint } from '@/lib/fairtrade-history-data';

type FairtradeHistoryChartsProps = {
  points: FairtradeChartPoint[];
  daysEstimated?: boolean;
};

type ChartRow = FairtradeChartPoint & {
  value: number;
  prevValue: number | null;
  rangeMin: number;
  rangeMax: number;
};

type Theme = {
  stroke: string;
  fillTop: string;
  fillMid: string;
  fillBottom: string;
  glow: string;
  cursor: string;
  refMin: string;
  refMax: string;
  refAvg: string;
};

const XATS_THEME: Theme = {
  stroke: '#38bdf8',
  fillTop: 'rgba(56,189,248,0.42)',
  fillMid: 'rgba(14,165,233,0.14)',
  fillBottom: 'rgba(3,10,35,0)',
  glow: 'rgba(56,189,248,0.55)',
  cursor: 'rgba(125,211,252,0.35)',
  refMin: 'rgba(56,189,248,0.45)',
  refMax: 'rgba(125,211,252,0.55)',
  refAvg: 'rgba(186,230,253,0.35)',
};

const DAYS_THEME: Theme = {
  stroke: '#fbbf24',
  fillTop: 'rgba(251,191,36,0.38)',
  fillMid: 'rgba(245,158,11,0.12)',
  fillBottom: 'rgba(3,10,35,0)',
  glow: 'rgba(251,191,36,0.5)',
  cursor: 'rgba(253,224,71,0.35)',
  refMin: 'rgba(251,191,36,0.45)',
  refMax: 'rgba(252,211,77,0.55)',
  refAvg: 'rgba(253,230,138,0.35)',
};

function formatAxis(n: number): string {
  if (!Number.isFinite(n)) return '';
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    return `${k >= 10 ? Math.round(k) : Math.round(k * 10) / 10}k`;
  }
  return String(Math.round(n));
}

function formatPrice(n: number): string {
  return Math.round(n).toLocaleString('pt-BR');
}

function formatRange(min: number, max: number, unit: 'xats' | 'days'): string {
  if (min === max) return `${formatPrice(min)} ${unit}`;
  return `${formatPrice(min)} - ${formatPrice(max)} ${unit}`;
}

function formatTooltipDate(dateInt: number, fallback: string): string {
  if (!dateInt) return fallback;
  const ms = dateInt > 1e12 ? dateInt : dateInt * 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return fallback;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function formatVariation(current: number, previous: number | null): {
  label: string;
  tone: 'up' | 'down' | 'flat' | 'none';
} {
  if (previous == null || previous === 0) {
    return { label: '—', tone: 'none' };
  }
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.05) {
    return { label: '0% desde o registro anterior', tone: 'flat' };
  }
  const sign = pct > 0 ? '+' : '';
  return {
    label: `${sign}${pct.toFixed(1).replace('.', ',')}% desde o registro anterior`,
    tone: pct > 0 ? 'up' : 'down',
  };
}

function PremiumTooltip({
  active,
  payload,
  coordinate,
  currency,
  anchorRef,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
  coordinate?: { x?: number; y?: number };
  currency: 'xats' | 'days';
  anchorRef: RefObject<HTMLDivElement | null>;
}) {
  if (!active || !payload?.length || typeof document === 'undefined') return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const variation = formatVariation(point.value, point.prevValue);
  const rect = anchorRef.current?.getBoundingClientRect();
  const rawX = (rect?.left ?? 0) + (coordinate?.x ?? 0);
  const rawY = (rect?.top ?? 0) + (coordinate?.y ?? 0);
  const tooltipW = 248;
  const tooltipH = 148;
  const pad = 12;
  // Preferir à direita do ponto; se não couber, virar para a esquerda.
  let left = rawX + pad;
  if (left + tooltipW > window.innerWidth - pad) {
    left = Math.max(pad, rawX - tooltipW - pad);
  }
  // Preferir acima do ponto para não cobrir a curva.
  let top = rawY - tooltipH - pad;
  if (top < pad) {
    top = Math.min(rawY + pad, window.innerHeight - tooltipH - pad);
  }

  const isXats = currency === 'xats';
  const priceLabel = isXats ? 'Preço em xats' : 'Preço em days';
  const priceValue = formatRange(point.rangeMin, point.rangeMax, currency);

  return createPortal(
    <div
      className={cn(
        'pointer-events-none fixed z-[99999] min-w-[220px] max-w-[260px] rounded-xl border px-3.5 py-3',
        'bg-[#07103a]/92 shadow-[0_16px_48px_rgba(0,0,0,0.55),0_0_28px_rgba(56,189,248,0.14)]',
        'backdrop-blur-md',
        isXats ? 'border-sky-400/25' : 'border-amber-400/25'
      )}
      style={{ left, top }}
      role="tooltip"
    >
      <p
        className="font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: 'rgba(186, 230, 253, 0.8)' }}
      >
        Data
      </p>
      <p className="mt-0.5 font-poppins text-sm font-semibold text-white">
        {formatTooltipDate(point.dateInt, point.fullDate)}
      </p>

      <div className="mt-3 space-y-2.5 border-t border-white/[0.08] pt-2.5">
        <div>
          <p
            className="mb-0.5 inline-flex items-center gap-1 font-poppins text-[10px] font-semibold uppercase tracking-wide"
            style={{ color: isXats ? 'rgba(186, 230, 253, 0.75)' : 'rgba(253, 230, 138, 0.8)' }}
          >
            {isXats ? <MoXat className="h-3 w-3" /> : <DaysStarIcon className="h-3 w-3" />}
            {priceLabel}
          </p>
          <p
            className="font-poppins text-sm font-semibold tabular-nums"
            style={{ color: isXats ? '#f0f9ff' : '#fffbeb' }}
          >
            {priceValue}
          </p>
        </div>

        <div>
          <p
            className="mb-0.5 font-poppins text-[10px] font-semibold uppercase tracking-wide"
            style={{ color: 'rgba(186, 230, 253, 0.8)' }}
          >
            Variação
          </p>
          <p
            className="font-poppins text-xs font-semibold"
            style={{
              color:
                variation.tone === 'up'
                  ? '#6ee7b7'
                  : variation.tone === 'down'
                    ? '#fda4af'
                    : variation.tone === 'flat'
                      ? 'rgba(186, 230, 253, 0.9)'
                      : 'rgba(186, 230, 253, 0.55)',
            }}
          >
            {variation.label}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ActiveDot(props: {
  cx?: number;
  cy?: number;
  currency?: 'xats' | 'days';
  glow?: string;
}) {
  const { cx, cy, currency = 'xats', glow } = props;
  if (cx == null || cy == null) return null;

  const size = 18;
  const half = size / 2;
  const softGlow =
    glow ??
    (currency === 'xats' ? 'rgba(56,189,248,0.55)' : 'rgba(251,191,36,0.5)');

  return (
    <g style={{ pointerEvents: 'none' }}>
      <circle cx={cx} cy={cy} r={12} fill={softGlow} opacity={0.3} />
      {currency === 'xats' ? (
        <image
          href={XATS_ICON_SRC}
          x={cx - half}
          y={cy - half}
          width={size}
          height={size}
          preserveAspectRatio="xMidYMid meet"
        />
      ) : (
        <g transform={`translate(${cx - half}, ${cy - half}) scale(${size / 24})`}>
          <polygon
            points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
            fill="#fbbf24"
            stroke="#f59e0b"
            strokeWidth={1.25}
            strokeLinejoin="round"
          />
        </g>
      )}
    </g>
  );
}

function StatBadges({
  min,
  max,
  avg,
  unit,
}: {
  min: number;
  max: number;
  avg: number;
  unit: 'xats' | 'days';
}) {
  const CurrencyIcon = unit === 'xats' ? MoXat : DaysStarIcon;
  const items = [
    { label: 'Mínimo', value: min },
    { label: 'Médio', value: avg },
    { label: 'Máximo', value: max },
  ];

  return (
    <div className="mb-2 flex flex-wrap gap-1.5 px-0.5">
      {items.map((item) => (
        <span
          key={item.label}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 font-poppins text-[10px] font-medium text-sky-100/85 backdrop-blur-sm"
        >
          {item.label}:
          <span className="inline-flex items-center gap-1 font-semibold tabular-nums text-white">
            <CurrencyIcon className="h-3 w-3 shrink-0" />
            {formatPrice(item.value)} {unit}
          </span>
        </span>
      ))}
    </div>
  );
}

function PriceChart({
  title,
  icon,
  points,
  currency,
  theme,
  fillId,
}: {
  title: string;
  icon: ReactNode;
  points: FairtradeChartPoint[];
  currency: 'xats' | 'days';
  theme: Theme;
  fillId: string;
}) {
  const uid = useId().replace(/:/g, '');
  const gradientId = `${fillId}-${uid}`;
  const chartAnchorRef = useRef<HTMLDivElement>(null);

  const { rows, stats } = useMemo(() => {
    const filtered = points.filter((p) => {
      if (currency === 'xats') return Number.isFinite(p.xatsMid);
      return typeof p.daysMid === 'number' && Number.isFinite(p.daysMid);
    });

    const values = filtered.map((p) =>
      currency === 'xats' ? p.xatsMid : (p.daysMid as number)
    );
    const seriesMin = Math.min(...values);
    const seriesMax = Math.max(...values);
    const seriesAvg = values.reduce((a, b) => a + b, 0) / values.length;

    const rows: ChartRow[] = filtered.map((p, i) => {
      const value = currency === 'xats' ? p.xatsMid : (p.daysMid as number);
      const rangeMin = currency === 'xats' ? p.xatsMin : (p.daysMin as number);
      const rangeMax = currency === 'xats' ? p.xatsMax : (p.daysMax as number);
      const prevValue =
        i > 0
          ? currency === 'xats'
            ? filtered[i - 1].xatsMid
            : (filtered[i - 1].daysMid as number)
          : null;
      return {
        ...p,
        value,
        prevValue,
        rangeMin,
        rangeMax,
      };
    });

    return {
      rows,
      stats: { min: seriesMin, max: seriesMax, avg: seriesAvg },
    };
  }, [points, currency]);

  if (rows.length < 2) {
    return (
      <div className="rounded-2xl border border-white/[0.08] bg-[#030023]/45 px-3.5 py-8 text-center">
        <p className="mb-1 flex items-center justify-center gap-1.5 font-poppins text-[11px] font-semibold uppercase tracking-wide text-sky-200/75">
          {icon}
          {title}
        </p>
        <p className="font-inter text-xs text-white/45">
          Dados insuficientes para desenhar o gráfico.
        </p>
      </div>
    );
  }

  const yPad = Math.max((stats.max - stats.min) * 0.18, stats.avg * 0.03, 1);

  return (
    <div
      className={cn(
        'rounded-2xl border border-white/[0.08] p-3 sm:p-3.5',
        'bg-gradient-to-b from-white/[0.05] to-white/[0.015]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2 px-0.5">
        <p className="flex items-center gap-1.5 font-poppins text-[11px] font-semibold uppercase tracking-wide text-sky-200/85">
          {icon}
          {title}
        </p>
      </div>

      <StatBadges
        min={stats.min}
        max={stats.max}
        avg={stats.avg}
        unit={currency}
      />

      <div ref={chartAnchorRef} className="relative w-full overflow-visible">
        <div className="h-[230px] w-full min-w-0 overflow-visible sm:h-[250px]">
          <ResponsiveContainer width="100%" height="100%" debounce={50}>
            <ComposedChart
              data={rows}
              margin={{ top: 12, right: 28, left: 4, bottom: 8 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.fillTop} />
                  <stop offset="48%" stopColor={theme.fillMid} />
                  <stop offset="100%" stopColor={theme.fillBottom} />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="rgba(148,163,184,0.08)"
                strokeDasharray="3 8"
                vertical={false}
              />

              <XAxis
                dataKey="label"
                tick={{
                  fill: 'rgba(186,230,253,0.55)',
                  fontSize: 10,
                  fontFamily: 'Poppins, sans-serif',
                }}
                tickLine={false}
                axisLine={false}
                minTickGap={24}
                dy={4}
                interval="preserveStartEnd"
                padding={{ left: 12, right: 16 }}
              />
              <YAxis
                tick={{
                  fill: 'rgba(148,163,184,0.7)',
                  fontSize: 10,
                  fontFamily: 'Poppins, sans-serif',
                }}
                tickLine={false}
                axisLine={false}
                width={44}
                tickFormatter={formatAxis}
                domain={[
                  (dataMin: number) => Math.max(0, dataMin - yPad),
                  (dataMax: number) => dataMax + yPad,
                ]}
              />

              <Tooltip
                content={(props) => (
                  <PremiumTooltip
                    active={props.active}
                    payload={props.payload as Array<{ payload: ChartRow }> | undefined}
                    coordinate={props.coordinate}
                    currency={currency}
                    anchorRef={chartAnchorRef}
                  />
                )}
                cursor={{
                  stroke: theme.cursor,
                  strokeWidth: 1,
                  strokeDasharray: '4 6',
                }}
                // Conteúdo real vai para portal em document.body (acima do modal).
                wrapperStyle={{
                  outline: 'none',
                  pointerEvents: 'none',
                  zIndex: 0,
                  overflow: 'visible',
                }}
                allowEscapeViewBox={{ x: true, y: true }}
                isAnimationActive={false}
              />

              <ReferenceLine
                y={stats.min}
                stroke={theme.refMin}
                strokeDasharray="4 6"
                strokeWidth={1}
                ifOverflow="discard"
              />
              <ReferenceLine
                y={stats.max}
                stroke={theme.refMax}
                strokeDasharray="4 6"
                strokeWidth={1}
                ifOverflow="discard"
              />
              <ReferenceLine
                y={stats.avg}
                stroke={theme.refAvg}
                strokeDasharray="2 5"
                strokeWidth={1}
                ifOverflow="discard"
              />

              <Area
                type="monotone"
                dataKey="value"
                name={currency === 'xats' ? 'Xats' : 'Days'}
                stroke={theme.stroke}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill={`url(#${gradientId})`}
                fillOpacity={1}
                dot={false}
                activeDot={<ActiveDot currency={currency} glow={theme.glow} />}
                isAnimationActive
                animationDuration={900}
                animationEasing="ease-out"
                animationBegin={80}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default function FairtradeHistoryCharts({
  points,
  daysEstimated = false,
}: FairtradeHistoryChartsProps) {
  const hasDays = points.some((p) => p.daysMid != null);

  if (points.length < 2) {
    return (
      <p className="py-8 text-center font-inter text-sm text-white/50">
        Dados insuficientes para exibir gráficos.
      </p>
    );
  }

  return (
    <section className="space-y-3.5" aria-label="Gráficos do histórico fairtrade">
      {daysEstimated ? (
        <p className="font-inter text-[10px] leading-snug text-blue-100/50">
          Days estimados pela relação xats/days actual
        </p>
      ) : null}

      <div className="grid gap-3.5">
        <PriceChart
          title="Evolução em xats"
          icon={<MoXat className="h-3.5 w-3.5" />}
          points={points}
          currency="xats"
          theme={XATS_THEME}
          fillId="fairtrade-xats-premium"
        />
        {hasDays ? (
          <PriceChart
            title="Evolução em days"
            icon={<DaysStarIcon className="h-3.5 w-3.5" />}
            points={points}
            currency="days"
            theme={DAYS_THEME}
            fillId="fairtrade-days-premium"
          />
        ) : null}
      </div>
    </section>
  );
}
